import debounce from 'lodash.debounce'
import { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'

import Geocoder from '@js/common/geocoder'

import { CheckoutAddressPhoneSchema } from '../questions/checkout/CheckoutAddressPhoneAutocomplete'

interface Props {
  suggestions: google.maps.places.AutocompletePrediction[]
  onSuggestionSelect: (
    suggestion: google.maps.places.AutocompletePrediction,
    callback: (value: string) => void
  ) => void
}

export const StreetAutocomplete = ({
  children
}: {
  children(children: Props): JSX.Element
}) => {
  const {
    setValue,
    getValues,
    watch,
    setFocus
  } = useFormContext<CheckoutAddressPhoneSchema>()
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >(([] as unknown) as google.maps.places.AutocompletePrediction[])
  const country = getValues('country')
  const countryRef = useRef(country)
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(
    null
  )
  const autoCompleteServiceRef = useRef<google.maps.places.AutocompleteService | null>(
    null
  )

  useEffect(() => {
    if (window.google) {
      countryRef.current = country

      autoCompleteServiceRef.current = new window.google.maps.places.AutocompleteService()
      setSuggestions([])
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
    }
  }, [country])

  useEffect(() => {
    const getPredictionDebounced = debounce(
      (values: CheckoutAddressPhoneSchema) => {
        if (values.street && values.street.length >= 2) {
          autoCompleteServiceRef.current?.getPlacePredictions(
            {
              input: values.street,
              sessionToken: sessionTokenRef.current,
              componentRestrictions: { country: countryRef.current },
              types: ['street_address', 'route']
            },
            predictions => {
              if (predictions) {
                const items = predictions.slice(0, 5)

                const itemsWithDescription = items.map(item => {
                  const descriptionWithoutCountry = item.description.replace(
                    /, Deutschland|, Österreich|, Schweiz|, Italien|, Anderes Land/g,
                    ''
                  )

                  return { ...item, description: descriptionWithoutCountry }
                }, [])
                setSuggestions(itemsWithDescription)
              }
            }
          )
        }
      },
      300
    )

    const subscription = watch(values => {
      getPredictionDebounced(values as CheckoutAddressPhoneSchema)
    })
    return () => subscription.unsubscribe()
  }, [watch])

  const getPlaceDetails = (
    placeId: string,
    callback: (value: string) => void
  ) => {
    const geocoder = new Geocoder()
    geocoder.placesService.getDetails(
      {
        placeId: placeId,
        sessionToken: sessionTokenRef.current as google.maps.places.AutocompleteSessionToken,
        fields: ['address_components', 'geometry']
      },
      (place, status) => {
        if (
          status === window.google.maps.places.PlacesServiceStatus.OK &&
          place
        ) {
          const addressComponents = {
            street_number: 'long_name',
            route: 'long_name',
            locality: 'long_name',
            postal_code: 'long_name',
            country: 'short_name'
          }

          const address: typeof addressComponents = place.address_components?.reduce(
            (acc: Partial<typeof addressComponents>, component) => {
              const componentType = component.types[0]
              if (addressComponents.hasOwnProperty(componentType)) {
                acc[componentType] = component[addressComponents[componentType]]
              }
              return acc
            },
            {}
          )

          const setValueOptions = {
            shouldDirty: true,
            shouldValidate: true,
            shouldTouch: true
          }

          setValue('city', address.locality, setValueOptions)
          setValue('street', address.route, setValueOptions)
          setValue('country', address.country, setValueOptions)
          if (address.postal_code) {
            setValue('postal_code', address.postal_code, setValueOptions)
          }
          if (address.street_number) {
            setValue('streetNumber', address.street_number, setValueOptions)
          } else {
            setFocus('streetNumber')
          }
          callback(address.route)
        }
      }
    )
  }

  const handleSuggestionSelect = (
    suggestion: google.maps.places.AutocompletePrediction,
    callback: (value: string) => void
  ) => {
    getPlaceDetails(suggestion.place_id, callback)
  }

  return children({ suggestions, onSuggestionSelect: handleSuggestionSelect })
}
